import { Fragment, useEffect, useState } from 'react';

import { AxiosError } from 'axios';
import moment from 'moment';
import { useForm } from 'react-hook-form';

import { useMultiStepsForm, useToast } from '@hooks';
import { requests } from 'common/axios';
import { getItems } from 'components/AvaEzm/Utilities/ODataUtility';
import { IPA } from 'components/ProviderData/IpaAdministration/atoms/Models';
import { asyncDebounce } from 'components/ProviderData/IpaAdministration/atoms/utils';
import styles from 'components/ProviderData/IpaAdministration/organisms/IpaAdministrationWizard/IpaAdministrationWizard.module.css';
import AhcPoi from 'components/ProviderData/IpaAdministration/organisms/IpaAdministrationWizard/IpaAdministrationWizardAhcPoi';
import ContractInfo from 'components/ProviderData/IpaAdministration/organisms/IpaAdministrationWizard/IpaAdministrationWizardContractInfo';
import IpaDetailsInfo from 'components/ProviderData/IpaAdministration/organisms/IpaAdministrationWizard/IpaAdministrationWizardDetails';
import MemberProviderInfo from 'components/ProviderData/IpaAdministration/organisms/IpaAdministrationWizard/IpaAdministrationWizardMemberProviderInfo';
import MsoDetails from 'components/ProviderData/IpaAdministration/organisms/IpaAdministrationWizard/IpaAdministrationWizardMsoDetails';
import PbpInfo from 'components/ProviderData/IpaAdministration/organisms/IpaAdministrationWizard/IpaAdministrationWizardPBPInfo';
import { refreshTabService, refreshIpaFiltersService } from 'components/ProviderData/subjects/IpaAdministrationService';
import { dialogService } from 'subjects/common/DialogService';

type IpaCreateProps = {
    ipaId: string;
    isCopyToNew: boolean;
};

const IpaAdministrationWizard = (props: IpaCreateProps) => {
    const { ipaId, isCopyToNew } = props;
    const [ipa, setIpa] = useState<any>({});
    const [visitedSteps, setVisitedSteps] = useState<number[]>([0]);
    const { showSuccessToast, showErrorToast } = useToast();
    const [companyCodes, setCompanyCodes] = useState<string[]>([]);
    const [mso, setMso] = useState<any>({});
    const [contracts, setContracts] = useState<any[]>([]);
    const [risks, setRisks] = useState<any[]>([]);
    const [types, setTypes] = useState<any[]>([]);
    const [contractCodes, setContractCodes] = useState<any[]>([]);
    const [pbpList, setPbpList] = useState<string[]>([]);
    const [termed, setTermed] = useState<string>();
    const [loading, setLoading] = useState<boolean>();

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<IPA>();

    const stepTitles = ['Contract & MSO Info', 'IPA Details Info', 'Member ID & Provider Info', 'PBP Info'];
    const stepsElements = [
        <Fragment key={3}>
            <div className='infoStepsMainWrap'>
                <div className='infoStepsWrap'>
                    <p className='section-container-header'>Contract Info</p>
                    <ContractInfo
                        contracts={contracts}
                        risks={risks}
                        types={types}
                        errors={errors}
                        register={register}
                        setValue={setValue}
                        getValues={getValues}
                        fetchMso={fetchMso}
                        companyCodes={companyCodes}
                        fetchContracts={fetchContracts}
                        termed={termed}
                    />
                </div>
                <div className='infoStepsWrap'>
                    <p className='section-container-header'>MSO Info</p>
                    <MsoDetails
                        register={register}
                        mso={mso}
                    />
                </div>
            </div>
        </Fragment>,
        <IpaDetailsInfo
            key={1}
            asyncDebounce={asyncDebounce}
            checkIpaCode={checkIpaCode}
            risks={risks}
            types={types}
            errors={errors}
            setValue={setValue}
            getValues={getValues}
            termed={termed}
            register={register}
            mso={mso}
        />,
        <Fragment key={4}>
            <div className='infoStepsMainWrap'>
                <div className='infoStepsWrap'>
                    <p className='section-container-header'>Member Provider Info</p>
                    <MemberProviderInfo
                        register={register}
                        errors={errors}
                    />
                </div>

                <div className='infoStepsWrap'>
                    <p className='section-container-header'>AHC/Network Management Point of contact</p>
                    <AhcPoi
                        register={register}
                        getValues={getValues}
                        errors={errors}
                    />
                </div>
                {loading && (
                    <div>
                        <div className={styles['spinner-content']}></div>
                        <div className={`${styles['spinner']} spinner-border`}></div>
                    </div>
                )}
            </div>
        </Fragment>,
        <PbpInfo
            key={5}
            ipa={ipa}
            asyncDebounce={asyncDebounce}
            checkIpaCode={checkIpaCode}
            risks={risks}
            types={types}
            errors={errors}
            setValue={setValue}
            getValues={getValues}
            termed={termed}
            register={register}
            mso={mso}
            contractCodes={contractCodes}
            fetchPbpCodes={fetchPbpCodes}
            pbpList={pbpList}
            ipaPlan={null}
        />,
    ];

    const stepsData = stepTitles.map((title, index) => ({ title: title, value: index }));

    async function fetchCompanyCodes() {
        const _companyCodes = await getItems<string>('/api/MedicalGroup/GetCompanyCodes');
        setCompanyCodes(_companyCodes);
    }

    async function fetchMso(contractId) {
        let _mso: any = {};
        _mso = await getItems<string>(`/api/MedicalGroup/GetMsoDetails?contractId=${contractId}`);
        setMso(_mso);
        for (const m in _mso) {
            const field = m.charAt(0).toUpperCase() + m.slice(1);
            setValue(('mso' + field) as keyof IPA, _mso[field]);
        }
        setTermed(_mso.isTermed);
    }

    async function fetchContracts() {
        let _contracts: any = [];
        _contracts = await getItems<string>(`/api/MedicalGroup/GetAllIpaContracts`);
        setContracts(_contracts);
    }

    async function fetchRisks() {
        let _risks: any = [];
        _risks = await getItems<string>(`/api/MedicalGroup/GetIpaRisks`);
        setRisks(_risks);
    }

    async function fetchTypes() {
        let _types: any = [];
        _types = await getItems<string>(`/api/MedicalGroup/GetIpaTypes`);
        setTypes(_types);
    }

    async function fetchContractCodes() {
        let _contractCodes: any = [];
        _contractCodes = await getItems<string>(`/api/IpaPlan/GetContractCodes?companyCode=N/A`);
        setContractCodes(_contractCodes);
    }

    async function checkIpaCode(ipaCode, effDate) {
        const exist = await getItems<string>(
            `/api/medicalgroup/IpaCodeAlreadyExists?ipaCode=${ipaCode}&effDate=${effDate}`,
        );
        return !exist;
    }

    async function fetchPbpCodes(contractCode) {
        const response = await requests.get<string[]>(
            `/api/IpaPlan/GetPBPCodes?companyCode=${getValues().companyCode}&contractCode=${contractCode}`,
        );
        setPbpList(response);
    }

    const { currentStep, step, isFirstStep, isLastStep, next, back, goTo } = useMultiStepsForm(stepsElements);

    const setStepsVisit = (stepIndex: number) => {
        if (stepIndex != 0) {
            setVisitedSteps((prevStep: number[]) => {
                if (!prevStep.includes(stepIndex)) {
                    return [...prevStep, stepIndex];
                }
                return prevStep;
            });
        }
    };

    const saveContractInformation = async (data: IPA, e) => {
        const selectedStep = stepsData.filter((f) => f.value == parseInt(e.nativeEvent.submitter.id));
        if (selectedStep.length > 0) {
            if (
                (visitedSteps.length >= selectedStep[0]?.value || visitedSteps.length === stepTitles.length) &&
                (selectedStep[0]?.value <= currentStep + 1 || visitedSteps.length === stepTitles.length)
            ) {
                goTo(selectedStep[0]?.value);
                setStepsVisit(selectedStep[0]?.value);
            }
        } else if (!isLastStep) {
            next();
            setStepsVisit(currentStep + 1);
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        } else if (data) {
            setLoading(true);
            try {
                let action = 'AddNewIpa';
                const effDt = new Date(data.effDate);
                const endDt = new Date(data.endDate);
                const adaptedData = {
                    ...data,
                    effDate: `${effDt.getMonth() + 1}/${effDt.getDate()}/${effDt.getFullYear()}`,
                    endDate: `${endDt.getMonth() + 1}/${endDt.getDate()}/${endDt.getFullYear()}`,
                };
                for (const d in adaptedData) {
                    if (d.includes('mso')) {
                        delete adaptedData[d];
                    }
                }
                if (ipa.id && !isCopyToNew) {
                    action = 'UpdateIpa';
                    adaptedData.id = ipa.id;
                }
                adaptedData.termed = false;
                if (action === 'AddNewIpa') adaptedData.companyCode = undefined;
                const url = `/api/MedicalGroup/${action}`;
                await requests.post(url, adaptedData);
                showSuccessToast(`IPA ${ipa.id ? 'Updated' : 'Created'} Successfully`);
                dialogService.close();
                refreshTabService.performAction('refresh');
                refreshIpaFiltersService.refresh();
                setLoading(false);
            } catch (e) {
                console.error(e);
                const message = `${(e as AxiosError)?.response?.data}`;
                showErrorToast(message);
                setLoading(false);
            }
        }
    };
    const cleanPhoneNumber = (phoneNumber) => phoneNumber.replace(/[()\- ]/g, '');

    useEffect(() => {
        setLoading(true);
        const controller = new AbortController();
        Promise.all([fetchCompanyCodes(), fetchRisks(), fetchTypes(), fetchContracts(), fetchContractCodes()]).finally(
            () => {
                setLoading(false);
            },
        );
        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (ipaId && getValues('contractLevelId')) {
            fetchMso(getValues('contractLevelId'));
        }
    }, [getValues('contractLevelId')]);

    useEffect(() => {
        if (companyCodes.length && risks.length && types.length) {
            if (ipaId) {
                requests.get(`/api/MedicalGroup/GetDetailByIpaId?ipaId=${ipaId}`).then((_ipa: any) => {
                    if (_ipa) {
                        setIpa(_ipa);
                        for (const field in _ipa) {
                            setValue(field as keyof IPA, _ipa[field]);
                        }
                        setValue('npi', _ipa.vendorNpi || _ipa.npi);
                        setValue('effDate', _ipa.effDate);
                        setValue('endDate', _ipa.endDate);
                        setValue('ipaPhone', cleanPhoneNumber(_ipa.ipaPhone));
                        setValue('ipaPhone2', cleanPhoneNumber(_ipa.ipaPhone2));
                        setLoading(false);
                    }
                });
            } else {
                setValue('effDate', moment(new Date()).format());
                setValue('endDate', moment(new Date(9999, 11, 31)).format());
                setLoading(false);
            }
        }
    }, [companyCodes, risks, types, ipaId]);

    return (
        <>
            <form
                className='mainFormWrapper'
                onSubmit={handleSubmit(saveContractInformation)}
            >
                {!loading ? (
                    <>
                        <div className='stepsWrapper'>
                            {stepsData &&
                                stepsData.map((item, i) => (
                                    <div
                                        className={`${
                                            i === currentStep
                                                ? 'arrowBox-active arrowActiveright '
                                                : 'arrowBox-Inactive arrow-right'
                                        } arrowBox`}
                                        key={i}
                                    >
                                        <input
                                            className='customStepsBtn'
                                            type='submit'
                                            id={i.toString()}
                                            value={item.title}
                                        />
                                    </div>
                                ))}
                        </div>

                        <div> {step} </div>

                        <div
                            className='buttonsWrapper'
                            style={{ paddingBottom: '10px' }}
                        >
                            {!isFirstStep && (
                                <input
                                    type='button'
                                    className='btn btn-primary btn-flat btn-sm mx-1'
                                    value='Back'
                                    formNoValidate={true}
                                    onClick={back}
                                />
                            )}
                            <input
                                type='submit'
                                className='btn btn-primary btn-flat btn-sm mx-1'
                                value={isLastStep ? 'Save' : 'Next'}
                                disabled={isLastStep && loading}
                            />
                        </div>
                    </>
                ) : (
                    <div>
                        <div className={styles['spinner-content']}></div>
                        <div className={`${styles['spinner']} spinner-border`}></div>
                    </div>
                )}
            </form>
        </>
    );
};

export default IpaAdministrationWizard;
